/**
 * CustomerPicker (SEARCH-FIRST-PICKERS, Seth 2026-08-20: "Right now I can
 * only transfer by customer number which I would have to know ahead of time
 * rather than being able to search and match. That should be common in
 * anything where I am transferring, moving, etc.")
 *
 * One reusable control: type ANYTHING (name, address, phone, customer #),
 * pick from live results, get the custno. Backed by the same
 * useCustomerSearch the top search bar uses — including the saturation fix
 * and Pegasus-native customers. Drop it anywhere a raw "customer #" input
 * used to live.
 */
import { useEffect, useRef, useState } from "react";
import { Search, X, User } from "lucide-react";
import { cn } from "@/lib/cn";
import { useCustomerSearch } from "@/features/customer-360/api";
import { displayCustomerName } from "@/lib/format";

export interface PickedCustomer {
  custno: string;
  name: string;
  address: string | null;
  city: string | null;
  /** Best phone on file — location phone first, then the account's. */
  phone?: string | null;
}

export function CustomerPicker({
  onSelect,
  selected,
  onClear,
  placeholder = "Search name, address, phone, or customer #…",
  excludeCustno,
  autoFocus,
}: {
  onSelect: (c: PickedCustomer) => void;
  /** Controlled selected value — renders as a chip with an ✕. */
  selected?: PickedCustomer | null;
  onClear?: () => void;
  placeholder?: string;
  excludeCustno?: string | null;
  autoFocus?: boolean;
}) {
  const [q, setQ] = useState("");
  const [debounced, setDebounced] = useState("");
  const [open, setOpen] = useState(false);
  const boxRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const t = window.setTimeout(() => setDebounced(q), 250);
    return () => window.clearTimeout(t);
  }, [q]);

  const { data: results, isFetching } = useCustomerSearch(debounced.length >= 2 ? debounced : "");

  useEffect(() => {
    const onDoc = (e: MouseEvent) => {
      if (boxRef.current && !boxRef.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, []);

  const rows = (results ?? [])
    .filter((r) => !excludeCustno || r.custno !== excludeCustno)
    .slice(0, 8);

  const pick = (r: (typeof rows)[number]) => {
    const loc = r.locations?.[0];
    onSelect({
      custno: r.custno,
      name: displayCustomerName(null, r.fullname) || r.custno,
      address: loc?.add1 ?? null,
      city: loc?.city ?? null,
      phone: loc?.phone1 ?? r.phone1 ?? null,
    });
    setQ("");
    setOpen(false);
  };

  if (selected) {
    return (
      <span className="inline-flex max-w-full items-center gap-2 rounded-lg border border-brand-200 bg-brand-50 px-2.5 py-1.5 text-sm">
        <User className="h-3.5 w-3.5 shrink-0 text-brand-500" />
        <span className="min-w-0 truncate">
          <span className="font-semibold text-slate-800">{selected.name}</span>
          {selected.address && <span className="text-slate-500"> · {selected.address}{selected.city ? `, ${selected.city}` : ""}</span>}
          <span className="text-xs text-slate-400"> · #{selected.custno}</span>
        </span>
        {onClear && (
          <button type="button" onClick={onClear} title="Pick someone else"
            className="shrink-0 rounded p-0.5 text-slate-400 transition-colors hover:bg-white hover:text-slate-600">
            <X className="h-3.5 w-3.5" />
          </button>
        )}
      </span>
    );
  }

  return (
    <div ref={boxRef} className="relative">
      <div className="relative">
        <Search className="pointer-events-none absolute left-2.5 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
        <input
          value={q}
          autoFocus={autoFocus}
          onChange={(e) => { setQ(e.target.value); setOpen(true); }}
          onFocus={() => setOpen(true)}
          onKeyDown={(e) => {
            if (e.key === "Enter" && rows.length > 0) { e.preventDefault(); pick(rows[0]); }
            if (e.key === "Escape") setOpen(false);
          }}
          placeholder={placeholder}
          className="w-full rounded-md border border-hairline py-1.5 pl-9 pr-3 text-sm focus:border-brand-500 focus:outline-none focus:ring-2 focus:ring-brand-500/30"
        />
      </div>
      {open && debounced.length >= 2 && (
        <div className="absolute z-30 mt-1 max-h-72 w-full overflow-y-auto rounded-lg border border-hairline bg-white shadow-e2">
          {rows.map((r) => {
            const loc = r.locations?.[0];
            return (
              <button
                key={r.custno}
                type="button"
                onClick={() => pick(r)}
                className="flex w-full items-start gap-2 px-3 py-2 text-left text-sm transition-colors hover:bg-brand-50/50"
              >
                <User className="mt-0.5 h-3.5 w-3.5 shrink-0 text-slate-300" />
                <span className="min-w-0">
                  <span className="block truncate font-medium text-slate-800">{displayCustomerName(null, r.fullname) || r.custno}</span>
                  <span className="block truncate text-xs text-slate-400">
                    {loc ? `${loc.add1 ?? ""}${loc.city ? `, ${loc.city}` : ""} · ` : ""}#{r.custno}
                  </span>
                </span>
              </button>
            );
          })}
          {rows.length === 0 && (
            <div className={cn("px-3 py-2.5 text-sm text-slate-400")}>
              {isFetching ? "Searching…" : "No matches — try fewer words, a phone number, or the address."}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
